// @ts-nocheck
import { PlayerDetailsFields } from "@/components/extras/PlayerExtraDialog";
/**
 * Everything about one extras purchase, in one panel.
 *
 * The grid can only show so many columns, and a video order carries more than
 * fits: who it's for, where to tag them, whether the content was shot and sent.
 * Sale facts (price, payment, Stripe session, QR) are read-only here — they
 * came from the checkout, not from the sheet.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const money = (v: unknown) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

const dateOnly = (v: unknown) => (v ? String(v).slice(0, 10) : "");
const stamp = (v: unknown) => (v ? new Date(String(v)).toLocaleString() : "—");

const FULFILMENT = [
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "delivered", label: "Delivered" },
];

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="mt-0.5 text-sm break-words">{children}</div>
  </div>
);

export default function ExtraDetailDialog({
  open,
  onOpenChange,
  row,
  canWrite,
  onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  row: any | null;
  canWrite?: boolean;
  onSave?: (id: string, patch: Record<string, unknown>) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setDraft({
      player_name: row.player_name ?? "",
      player_jersey_number: row.player_jersey_number ?? "",
      player_instagram_handle: row.player_instagram_handle ?? "",
      player_x_handle: row.player_x_handle ?? "",
      game_date: dateOnly(row.game_date),
      fulfilment_status: row.fulfilment_status ?? "not_started",
      fulfilment_notes: row.fulfilment_notes ?? "",
    });
  }, [row?.id, open]);

  if (!row) return null;

  const set = (k: string, v: unknown) => setDraft((d) => ({ ...d, [k]: v }));

  const dirty = Object.keys(draft).some((k) => {
    const original = k === "game_date" ? dateOnly(row[k]) : row[k] ?? (k === "fulfilment_status" ? "not_started" : "");
    return String(draft[k] ?? "") !== String(original ?? "");
  });

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    const res = await onSave(row.id, {
      ...draft,
      game_date: draft.game_date || null,
    });
    setSaving(false);
    if (res?.ok) onOpenChange(false);
  };

  const paid = String(row.payment_status ?? "").toLowerCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {row.extra_name}
            <span className="text-muted-foreground font-normal">{money(row.extra_price)}</span>
            <Badge
              variant={paid === "paid" || paid === "free" ? "default" : "secondary"}
              className="capitalize"
            >
              {row.payment_status || "unpaid"}
            </Badge>
            {row.is_redeemed && <Badge variant="outline">Redeemed</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* The sale — from checkout, not editable here. */}
          <section className="grid grid-cols-2 gap-4 rounded-md border bg-muted/30 p-4">
            <Field label="Team">{row.team_name || "—"}</Field>
            <Field label="Division">{row.division || "—"}</Field>
            <Field label="Buyer email">{row.buyer_email || "—"}</Field>
            <Field label="Purchased">{stamp(row.created_at)}</Field>
            <Field label="Stripe session">
              <span className="font-mono text-xs">{row.stripe_session_id || "—"}</span>
            </Field>
            <Field label="QR code">
              <span className="font-mono text-xs">{row.qr_code || "—"}</span>
            </Field>
            <Field label="Redemption">
              {row.is_redeemed
                ? `Redeemed ${stamp(row.redeemed_at)}${row.redeemed_by ? ` by ${row.redeemed_by}` : ""}`
                : "Not redeemed"}
            </Field>
            <Field label="Game">
              {[dateOnly(row.game_date) || null, row.game_label || null]
                .filter(Boolean)
                .join(" · ") || "—"}
            </Field>
          </section>

          {/* Who it's for. */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Who it's for</h3>
            <PlayerDetailsFields
              idPrefix="ed"
              disabled={!canWrite}
              allowFreeTextPlayer
              name={draft.player_name ?? ""}
              jersey={draft.player_jersey_number ?? ""}
              ig={draft.player_instagram_handle ?? ""}
              x={draft.player_x_handle ?? ""}
              onChange={(patch) => {
                if (patch.name !== undefined) set("player_name", patch.name);
                if (patch.jersey !== undefined) set("player_jersey_number", patch.jersey);
                if (patch.ig !== undefined) set("player_instagram_handle", patch.ig);
                if (patch.x !== undefined) set("player_x_handle", patch.x);
              }}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Game date</Label>
                <Input
                  type="date"
                  className="mt-1"
                  disabled={!canWrite}
                  value={draft.game_date ?? ""}
                  onChange={(e) => set("game_date", e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Fulfilment — the production side of the sale. */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Fulfilment</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select
                  value={draft.fulfilment_status ?? "not_started"}
                  disabled={!canWrite}
                  onValueChange={(v) => set("fulfilment_status", v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FULFILMENT.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {row.fulfilled_at && (
                <Field label="Delivered">{stamp(row.fulfilled_at)}</Field>
              )}
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                className="mt-1"
                rows={3}
                disabled={!canWrite}
                placeholder="Shoot details, delivery link, who's producing it…"
                value={draft.fulfilment_notes ?? ""}
                onChange={(e) => set("fulfilment_notes", e.target.value)}
              />
            </div>
          </section>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {canWrite && (
              <Button onClick={save} disabled={saving || !dirty}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
